import {
  DefaultTheme,
  createGlobalStyle,
  ThemeProvider as StyledThemeProvider,
} from 'styled-components';
import React, { ExoticComponent, FC, PropsWithChildren } from 'react';
import { lightenColor } from '@idk-web/core-utils';
import { InboxBrandResponse } from '@idk-web/api';
import { defaultTheme } from '@/styled-components/theme';

function getBrandTheme(brand: InboxBrandResponse): DefaultTheme {
  const theme = JSON.parse(JSON.stringify(defaultTheme));

  if (brand.primaryAccentColor) {
    theme.palette.primary.main = brand.primaryAccentColor;
    theme.palette.primary.light1 = lightenColor(brand.primaryAccentColor, 0.1);
    theme.palette.primary.light2 = lightenColor(brand.primaryAccentColor, 0.2);
    theme.palette.primary.light3 = lightenColor(brand.primaryAccentColor, 0.3);
    theme.palette.primary.light4 = lightenColor(brand.primaryAccentColor, 0.4);
    theme.palette.primary.light5 = lightenColor(brand.primaryAccentColor, 0.5);
  }

  if (brand.secondaryAccentColor) {
    theme.palette.secondary.main = brand.secondaryAccentColor;
    theme.palette.secondary.light1 = lightenColor(
      brand.secondaryAccentColor,
      0.1,
    );
    theme.palette.secondary.light2 = lightenColor(
      brand.secondaryAccentColor,
      0.2,
    );
    theme.palette.secondary.light3 = lightenColor(
      brand.secondaryAccentColor,
      0.3,
    );
    theme.palette.secondary.light4 = lightenColor(
      brand.secondaryAccentColor,
      0.4,
    );
  }

  if (brand.font) {
    theme.typography.h1.fontFamily = `CustomBrandFont, OpenSans, sans-serif`;
    theme.typography.h2.fontFamily = `CustomBrandFont, OpenSans, sans-serif`;
    theme.typography.h3.fontFamily = `CustomBrandFont, OpenSans, sans-serif`;
    theme.typography.h4.fontFamily = `CustomBrandFont, OpenSans, sans-serif`;
    theme.typography.h5.fontFamily = `CustomBrandFont, OpenSans, sans-serif`;
  }

  return theme;
}

function getBrandStyle(brand: InboxBrandResponse): ExoticComponent {
  if (!brand.font) {
    return createGlobalStyle``;
  }

  return createGlobalStyle`
    @font-face {
      font-family: 'CustomBrandFont';
      src: url(/${brand.font});
    }
  `;
}

export type BrandThemeProviderProps = {
  brand: InboxBrandResponse | null;
};

export const BrandThemeProvider: FC<
  PropsWithChildren<BrandThemeProviderProps>
> = ({ brand, children }) => {
  if (!brand) {
    return children;
  }

  const BrandStyle = getBrandStyle(brand);
  const theme = getBrandTheme(brand);

  return (
    <StyledThemeProvider theme={theme}>
      <BrandStyle />
      {children}
    </StyledThemeProvider>
  );
};
